import Color from 'color';
import colors from '../styles/colors';
import { trackError } from '../components/common/error.utils';

function isCssVar(color: string | undefined): color is string {
    return !!color?.trim().startsWith('var(');
}

export function nextColorInScale(color: string) {
    if (!color) {
        return null;
    }

    const matchFirstNumber = color.match(/[0-9]/);

    if (!matchFirstNumber) {
        return null;
    }

    const indexOfFirstNumber = matchFirstNumber.index;

    if (!indexOfFirstNumber) {
        return null;
    }

    const scale = color.substring(0, indexOfFirstNumber);
    const number = Number(color.substring(indexOfFirstNumber));

    if (!scale || !number) {
        return null;
    }

    const nextInScaleKey1 = `${scale}${number + 10}`;

    if (colors[nextInScaleKey1]) {
        return nextInScaleKey1;
    }
    const nextInScaleKey2 = `${scale}${number + 100}`;
    if (colors[nextInScaleKey2]) {
        return nextInScaleKey2;
    }

    return null;
}

export function lighten(color, value) {
    if (isCssVar(color)) {
        return color;
    }
    return Color(color).lighten(value).hex();
}

export function darken(color, value: number) {
    if (isCssVar(color)) {
        // color-mix mixes toward black; value 0.3 → 70% original, 30% black
        return `color-mix(in oklch, ${color} ${Math.round(
            (1 - value) * 100
        )}%, black)`;
    }
    if (!color.startsWith('rgba')) {
        return Color(color).darken(value).hex();
    }
    // if it's rgba with transparency - add the value to the alpha
    const newAlpha = Color(color).rgb().round().array()[3] + value;
    if (newAlpha > 1) {
        // if the summed transparency is bigger than one - fallback to regular darkening
        return Color(color).darken(value).hex();
    }
    return Color(color).alpha(newAlpha).toString();
}

export function isTransparent(color: string) {
    return color === 'rgba(0, 0, 0, 0)' || color === 'transparent';
}

export function isOpaque(color: string) {
    return isValidColor(color) && Color(color).alpha() === 1;
}

export function isValidColor(color: string) {
    if (!color) {
        return false;
    }
    try {
        Color(color);
        return true;
    } catch {
        return false;
    }
}

// TBD this function name is still kept for backward compatibility purpose
export function opacityHex(hex: string, opacity: number) {
    if (hex) {
        const tempHex = hex.replace('#', '');
        const r = parseInt(tempHex.substring(0, 2), 16);
        const g = parseInt(tempHex.substring(2, 4), 16);
        const b = parseInt(tempHex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
    }
    return null;
}

/** Return a color with opacity using CSS color-mix().
 * Works with any CSS color format: rgb, rgba, hex, hsl, hsla, named colors, and CSS variables.
 * @param color - Any CSS color value (e.g., '#3cb878', 'rgb(10, 20, 30)', 'var(--hb-colors-surface-item)')
 * @param opacity - Opacity percentage (0-100). Note: This sets absolute opacity, not relative to existing opacity.
 * @returns CSS color-mix() expression
 */
export function applyOpacity(color: string, opacity: number): string | null {
    if (!color) {
        return null;
    }
    // CSS variables (var(...)) are not valid colors in the JavaScript Color library,
    // but they're valid in CSS, so we allow them through
    const isCssVariable = isCssVar(color);

    // For non-CSS-variables, validate the color format
    if (!isCssVariable && !isValidColor(color)) {
        return null;
    }

    // Use color-mix() for all cases - works with all CSS color formats
    return `color-mix(in srgb, ${color} ${opacity}%, transparent)`;
}

export function makeOpaque(color: string) {
    if (!isValidColor(color)) {
        // couldn't think of a better fallback, as i don't want to enforce a specific default color,
        // and most probably the color will be interpreted somehow by css.
        // eslint-disable-next-line no-console
        trackError(new Error(`@color.utils: provided color is not valid`), {
            color
        });
        return color;
    }
    return Color(color).alpha(1).rgb().string();
}

export function hexToRgbString(color: string) {
    if (!isValidColor(color)) {
        // eslint-disable-next-line no-console
        trackError(new Error(`@color.utils: provided color is not valid`), {
            color
        });
        return color;
    }
    return Color(color).rgb().array().join(', ');
}

/** Calculates the text color that should be used on a given background color.
 * If the background color is not valid, black will be returned by default.
 * @param backgroundColor
 */
export const calculateTextColorForBackground = (backgroundColor: string) => {
    if (!backgroundColor || !isValidColor(backgroundColor)) {
        return colors.textPrimary;
    }
    return Color(backgroundColor).isLight() ? colors.textPrimary : colors.white;
};
